/**
 * Structured Error Types
 * 
 * Defines error codes and recovery actions for consistent error handling
 */

/**
 * Error codes for different failure scenarios
 */
export enum ErrorCode {
  // Network errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  
  // API errors
  API_ERROR = 'API_ERROR',
  PAYMENT_REQUIRED = 'PAYMENT_REQUIRED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  NOT_FOUND = 'NOT_FOUND',
  
  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_RESPONSE = 'INVALID_RESPONSE',
  
  // Business logic errors
  CHECKOUT_ERROR = 'CHECKOUT_ERROR',
  CART_ERROR = 'CART_ERROR',
  PRODUCT_ERROR = 'PRODUCT_ERROR',
  
  // Generic
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Suggested recovery actions for different error types
 */
export enum RecoveryAction {
  RETRY = 'retry',
  REFRESH = 'refresh',
  CONTACT_SUPPORT = 'contact_support',
  GO_BACK = 'go_back',
  GO_HOME = 'go_home',
  NONE = 'none',
}

/**
 * Structured error interface with code, message, and recovery guidance
 */
export interface StructuredError {
  /** Error code for programmatic handling */
  code: ErrorCode;
  
  /** Human-readable error message */
  message: string;
  
  /** Optional detailed context about the error */
  details?: unknown;
  
  /** Suggested action for recovery */
  recoveryAction?: RecoveryAction;
  
  /** Timestamp when error occurred */
  timestamp?: Date;
}

/**
 * Create a structured error from various input types
 */
export function createStructuredError(
  error: unknown,
  code: ErrorCode = ErrorCode.UNKNOWN_ERROR,
  recoveryAction: RecoveryAction = RecoveryAction.NONE
): StructuredError {
  let message = 'An unexpected error occurred';
  let details: unknown = undefined;

  if (error instanceof Error) {
    message = error.message;
    details = {
      name: error.name,
      stack: error.stack,
    };
  } else if (typeof error === 'string') {
    message = error;
  } else if (error && typeof error === 'object') {
    details = error;
    if ('message' in error && typeof error.message === 'string') {
      message = error.message;
    }
  }

  return {
    code,
    message,
    details,
    recoveryAction,
    timestamp: new Date(),
  };
}

/**
 * Type guard to check if an error is a StructuredError
 */
export function isStructuredError(error: unknown): error is StructuredError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    Object.values(ErrorCode).includes((error as StructuredError).code)
  );
}

/**
 * Get user-friendly message from structured error
 */
export function getErrorMessage(error: StructuredError | unknown): string {
  if (isStructuredError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
}
